import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import campaignService, { isCampaignActive } from '../../services/campaignService';
import { useAuth } from '../../context/AuthContext';
import { useFeedback } from '../../contexts/FeedbackContext';
import LoadingSpinner from '../../components/LoadingSpinner';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'draft', label: 'Draft' },
  { value: 'completed', label: 'Completed' },
  { value: 'scheduled', label: 'Scheduled' },
];

const formatBudget = (budget) => {
  if (!budget) return '₱0';
  if (typeof budget === 'number') return `₱${budget.toLocaleString()}`;
  return `₱${budget.toString().replace(/[₱,]/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
};

const Campaigns = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentUser } = useAuth();
  const { showSuccess, showError } = useFeedback();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [duplicatingId, setDuplicatingId] = useState(null);
  
  // Helper function to format date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Not set';
    
    // If it's a Firebase timestamp, convert to JS Date
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };
  
  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Get campaigns for the current brand user
        const options = {};
        if (statusFilter !== 'all') {
          options.status = statusFilter;
        }
        
        const campaignsData = await campaignService.getCampaignsByBrandId(currentUser.uid, options);
        setCampaigns(campaignsData);
      } catch (err) {
        console.error('Error fetching campaigns:', err);
        setError('Failed to load campaigns. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    if (currentUser?.uid) {
      fetchCampaigns();
    }
  }, [currentUser?.uid, statusFilter]);
  
  const getStatusColor = (status, campaign) => {
    // For active campaigns, check if the end date has passed
    if (status === 'active') {
      // Use isCampaignActive to check if the campaign is truly active
      return isCampaignActive(campaign) 
        ? 'bg-green-100 text-green-800'  // Truly active
        : 'bg-red-100 text-red-800';    // End date has passed
    }
    
    // For other statuses
    switch (status) {
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'scheduled':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  const handleDeleteClick = (campaign) => {
    setCampaignToDelete(campaign);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!campaignToDelete) return;

    try {
      setDeleting(true);
      await campaignService.deleteCampaign(campaignToDelete.id);
      
      // Remove from local state
      setCampaigns(prev => prev.filter(c => c.id !== campaignToDelete.id));
      
      showSuccess('Campaign deleted successfully', 'The campaign has been removed from your account.', 3000);
      setShowDeleteModal(false);
      setCampaignToDelete(null);
    } catch (error) {
      console.error('Error deleting campaign:', error);
      showError('Failed to delete campaign.', 'Please check your connection and try again.', 5000);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setCampaignToDelete(null);
  };

  const handleDuplicate = async (campaign) => {
    try {
      setDuplicatingId(campaign.id);
      const original = await campaignService.getCampaignById(campaign.id);
      const now = new Date();
      const { id, analytics, filledSpots, metrics, trackingAnalytics, payment, ...rest } = original;
      const campaignData = {
        ...rest,
        name: `${original.name} (Copy)`,
        status: 'draft',
        filledSpots: 0,
        createdAt: now,
        updatedAt: now,
      };
      await campaignService.createCampaign(currentUser.uid, campaignData);
      showSuccess('Campaign duplicated successfully', `"${original.name} (Copy)" has been created as a draft.`, 3000);
      const campaignsData = await campaignService.getCampaignsByBrandId(currentUser.uid, statusFilter !== 'all' ? { status: statusFilter } : {});
      setCampaigns(campaignsData);
    } catch (error) {
      console.error('Error duplicating campaign:', error);
      showError('Failed to duplicate campaign.', 'Please check your connection and try again.', 5000);
    } finally {
      setDuplicatingId(null);
    }
  };

  const filteredCampaigns = campaigns
    .filter(campaign => campaign.name?.toLowerCase().includes(searchTerm.toLowerCase()))
    .filter(campaign => statusFilter === 'all' || campaign.status === statusFilter);
  
  const hasActiveFilters = searchTerm !== '' || statusFilter !== 'all';

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
  };

  return (
    <div>
      <div className="mb-6">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Campaigns</h2>
            <p className="text-gray-600">Manage your influencer marketing campaigns</p>
          </div>
          <Link
            to="/brand/campaigns/create"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200"
          >
            Create Campaign
          </Link>
        </div>
      </div>
      
      {/* Search and Filter Bar */}
      <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-100 p-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          {/* Search */}
          <div className="flex-1">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search campaigns by name..."
              className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
            />
          </div>
          {/* Status Filter */}
          <div className="flex flex-wrap gap-2">
            {STATUS_OPTIONS.map(option => (
              <button
                key={option.value}
                onClick={() => setStatusFilter(option.value)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  statusFilter === option.value
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
          {/* Clear */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-sm text-gray-500 hover:text-gray-700 px-2 py-1.5"
            >
              Clear
            </button>
          )}
        </div>
        {!loading && !error && (
          <p className="text-sm text-gray-500 mt-3">
            {filteredCampaigns.length} campaign{filteredCampaigns.length !== 1 ? 's' : ''} found
          </p>
        )}
      </div>

      {/* Campaign List */}
      <div className="mt-4">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <LoadingSpinner size="lg" color="primary" />
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-100 rounded-lg p-5 my-4 shadow-sm">
            <p className="text-sm font-medium text-red-700">{error}</p>
          </div>
        ) : filteredCampaigns.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-lg shadow-sm border border-gray-100">
            <p className="text-gray-600 mb-4">{hasActiveFilters ? 'No campaigns match your filters.' : 'No campaigns found. Create your first campaign!'}</p>
            {!hasActiveFilters && (
              <Link to="/brand/campaigns/create" className="mt-2 inline-flex items-center px-5 py-2.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark transition-colors duration-200">
                Create Campaign
              </Link>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredCampaigns.map((campaign) => {
              const spotsLeft = Math.max(0, (campaign.totalSpots || 0) - (campaign.filledSpots || 0));
              return (
                <div
                  key={campaign.id}
                  className="bg-white rounded-lg shadow-md border border-gray-100 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 flex flex-col"
                >
                  {/* Campaign Image or placeholder */}
                  <div className="w-full h-52 overflow-hidden rounded-t-lg relative bg-gray-100">
                    {campaign.imageUrl ? (
                      <img
                        src={campaign.imageUrl}
                        alt={campaign.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-primary/10 to-blue-400/10" />
                    )}
                    {/* Status badge */}
                    <span className={`absolute top-2 right-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium shadow-sm ${getStatusColor(campaign.status, campaign)}`}>
                      {campaign.status === 'active' && !isCampaignActive(campaign) ? 'Expired' : campaign.status}
                    </span>
                  </div>

                  <div className="p-4 flex-1 flex flex-col">
                    {/* Title */}
                    <h3 className="text-base font-semibold text-gray-900 line-clamp-1 mb-2">{campaign.name}</h3>

                    {/* Description */}
                    {campaign.description && (
                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">{campaign.description}</p>
                    )}

                    {/* Category badge */}
                    {campaign.category && (
                      <div className="mb-3">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {campaign.category}
                        </span>
                      </div>
                    )}

                    {/* Date */}
                    <div className="text-xs text-gray-500 mb-3">
                      {campaign.startDate ? formatDate(campaign.startDate) : 'Not set'} to {campaign.endDate ? formatDate(campaign.endDate) : 'Not set'}
                    </div>

                    {/* Compact info row */}
                    <div className="mt-auto pt-3 border-t border-gray-100 flex items-center justify-between text-sm">
                      <div>
                        <span className="text-gray-500">Budget:</span>{' '}
                        <span className="font-semibold text-gray-900">{formatBudget(campaign.budget)}</span>
                      </div>
                      <div className="text-right">
                        <span className="text-gray-500 text-xs block">Influencers</span>
                        <span className="font-semibold text-gray-900">{campaign.filledSpots || 0}</span>
                      </div>
                    </div>
                  </div>

                  <div className="p-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between gap-2">
                    <Link
                      to={`/brand/campaigns/${campaign.id}`}
                      className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark transition-colors"
                    >
                      View Details
                    </Link>
                    <div className="flex items-center gap-1">
                      <Link
                        to={`/brand/campaigns/${campaign.id}/edit`}
                        className="inline-flex items-center px-2.5 py-2 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                        title="Edit campaign"
                      >
                        Edit
                      </Link>
                      <button
                        onClick={() => handleDuplicate(campaign)}
                        disabled={duplicatingId === campaign.id}
                        className="inline-flex items-center px-2.5 py-2 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Duplicate campaign"
                      >
                        {duplicatingId === campaign.id ? '...' : 'Copy'}
                      </button>
                      <button
                        onClick={() => handleDeleteClick(campaign)}
                        className="inline-flex items-center px-2.5 py-2 border border-red-300 text-xs font-medium rounded-md text-red-700 bg-white hover:bg-red-50 transition-colors"
                        title="Delete campaign"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Delete Confirmation Modal */}
      {showDeleteModal && campaignToDelete && (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
        <div className="relative mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 max-w-lg shadow-lg rounded-md bg-white">
          <div className="mt-3">
            {/* Title */}
            <h3 className="text-lg font-medium text-gray-900 text-center mt-4">
              Delete Campaign?
            </h3>
            
            {/* Campaign Name */}
            <div className="mt-2 text-center">
              <p className="text-sm font-semibold text-gray-700">
                {campaignToDelete.name}
              </p>
            </div>
            
            {/* Warning Message */}
            <div className="mt-4 bg-red-50 border-l-4 border-red-400 p-4 rounded">
              <div>
                <h3 className="text-sm font-medium text-red-800">
                  Payment Forfeiture Warning
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>
                    Deleting this campaign will permanently remove all campaign data, including:
                  </p>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>All influencer applications</li>
                    <li>Submitted content and proofs</li>
                    <li>Analytics and tracking data</li>
                    <li>Campaign settings and requirements</li>
                  </ul>
                  <p className="mt-3 font-semibold">
                    Any payments made for this campaign will be forfeited and cannot be refunded.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Campaign Budget Info */}
            {campaignToDelete.budget && (
              <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded p-3">
                <p className="text-sm text-yellow-800">
                  <span className="font-semibold">Campaign Budget:</span>{' '}
                  {formatBudget(campaignToDelete.budget)}
                </p>
              </div>
            )}
            
            {/* Confirmation Text */}
            <div className="mt-4 text-center">
              <p className="text-sm text-gray-600">
                This action cannot be undone. Are you sure you want to proceed?
              </p>
            </div>
            
            {/* Action Buttons */}
            <div className="mt-6 flex justify-center space-x-3">
              <button
                onClick={handleDeleteCancel}
                disabled={deleting}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleting}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleting ? (
                  <>
                    <LoadingSpinner size="xs" color="white" />
                    <span className="ml-2">Deleting...</span>
                  </>
                ) : (
                  <>
                    Delete Campaign
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default Campaigns;
